import { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { OAuthButtons } from '@/components/auth/OAuthButtons';
import {
  TextField, PasswordField, OrDivider, FormError, SubmitButton,
} from '@/components/auth/FormFields';
import { authApi, readApiError } from '@/lib/auth-api';

export function SignupPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const qc = useQueryClient();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [agree, setAgree] = useState(true);
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!agree) {
      setErr(t('auth.error_must_agree_terms'));
      return;
    }
    setPending(true);
    try {
      await authApi.register({ email, name, password });
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
      nav('/app?welcome=1', { replace: true });
    } catch (e) {
      setErr(readApiError(e, t('auth.error_signup_failed')));
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthLayout title={t('auth.signup_title')} subtitle={t('auth.signup_subtitle')}>
      <OAuthButtons />
      <OrDivider label={t('auth.or_email')} />

      <form onSubmit={onSubmit} className="space-y-4">
        <FormError message={err} />
        <TextField
          id="name"
          label={t('auth.name_label')}
          placeholder={t('auth.name_placeholder')}
          value={name}
          onChange={setName}
          autoComplete="name"
          autoFocus
          required
        />
        <TextField
          id="email"
          type="email"
          inputMode="email"
          label={t('auth.email_label')}
          placeholder="cfo@acme.co"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          required
        />
        <PasswordField
          id="password"
          label={t('auth.password_label')}
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          showStrength
        />

        <label className="flex items-start gap-2.5 text-[12.5px] text-muted-foreground leading-relaxed cursor-pointer">
          <input
            type="checkbox"
            checked={agree}
            onChange={(e) => setAgree(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-border text-foreground focus:ring-foreground/20"
          />
          <span>
            {t('auth.terms_pre')}{' '}
            <Link to="/terms" className="text-foreground hover:underline">{t('footer.terms')}</Link>
            {' '}{t('common.and') ?? 'and'}{' '}
            <Link to="/privacy" className="text-foreground hover:underline">{t('footer.privacy')}</Link>.
          </span>
        </label>

        <SubmitButton pending={pending}>{t('auth.create_account')}</SubmitButton>

        <ul className="mt-1 text-[12px] text-muted-foreground space-y-1.5">
          {[t('auth.feature_10_seats'), t('auth.feature_no_card'), t('auth.feature_cancel_anytime')].map((s) => (
            <li key={s} className="flex items-center gap-2">
              <Check className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2.5} />
              {s}
            </li>
          ))}
        </ul>
      </form>

      <p className="mt-7 text-[13px] text-muted-foreground">
        {t('auth.already_have_account')}{' '}
        <Link to="/login" className="font-semibold text-foreground hover:underline">
          {t('auth.sign_in')}
        </Link>
      </p>
    </AuthLayout>
  );
}
